import { useCallback, useState } from 'react';
import { WebSettingPreset } from '../../../components/sitebase/Base.types.tsx';
import { MapSettingsService } from '../services/MapSettingsService.ts';
import { MapPresenter } from '../MapPresenter.ts';
import moment from 'moment';
import { LayerSelectorViews } from '../components/layerselector/LayerSelector.types';
import { AppShareState } from '../Map.types.ts';
import { encodeShareState } from '../shareStateCodec.ts';

export interface StateToStringOptions {
    includeForecast?: boolean;
    includeMapPosition?: boolean;
    includeLayers?: boolean;
    includeLayerSelectorView?: boolean;
    includeBaseMap?: boolean;
}

export function usePresets(
    presenter: MapPresenter,
    remote: MapSettingsService,
    initialPresets: WebSettingPreset,
    selectedBaseMap: number
) {
    const [presets, setPresets] = useState<WebSettingPreset>(initialPresets);

    const onSavePreset = useCallback((newPreset: WebSettingPreset) => {
        remote.updateUserWebSettings({ presets: { ...presets, ...newPreset } });
        setPresets({ ...presets, ...newPreset });
    }, [remote, presets]);

    // onSavePreset merges into the existing presets, so it cannot remove one; deleting builds a copy without it
    const onDeletePreset = useCallback((name: string) => {
        const { [name]: _removed, ...remaining } = presets;
        remote.updateUserWebSettings({ presets: remaining });
        setPresets(remaining);
    }, [remote, presets]);

    const stateToString = (
        state: AppShareState,
        options?: StateToStringOptions
    ) => {
        const payload: Partial<AppShareState> = {};

        if (options) {
            if (options.includeForecast && state.forecast) {
                payload.forecast = state.forecast;
            }
            if (options.includeMapPosition && state.c && state.z) {
                payload.c = state.c;
                payload.z = state.z;
            }
            if (options.includeLayers && state.layers) {
                payload.layers = state.layers;
            }
            if (options.includeLayerSelectorView && state.lview) {
                payload.lview = state.lview;
            }
            if (options.includeBaseMap && selectedBaseMap) {
                payload.base_map = selectedBaseMap.toString();
            }
        } else {
            if (state.forecast) {
                payload.forecast = state.forecast;
            }
            if (state.c && state.z) {
                payload.c = state.c;
                payload.z = state.z;
            }
            if (state.layers) {
                payload.layers = state.layers;
            }
            if (state.lview) {
                payload.lview = state.lview;
            }
            if (selectedBaseMap) {
                payload.base_map = selectedBaseMap.toString();
            }
        }

        return encodeShareState(payload);
    };

    const onLoadPreset = (
        presetValue: string,
        reloadAllLayers: (layersParam: string) => void,
        onSetForecastDate: (fd: string) => void,
        onSetLayerSelectorView: (view: LayerSelectorViews) => void,
    ) => {
        console.debug("Loading preset", presetValue);
        let presetState: Partial<AppShareState>;
        try {
            presetState = JSON.parse(decodeURIComponent(presetValue));
        } catch (e) {
            console.error("Failed to parse preset", e);
            return;
        }

        if (presetState.c && presetState.z) {
            const center = presetState.c.split(",").map(parseFloat);
            const zoom = parseFloat(presetState.z);
            presenter.map.getView().setCenter(center);
            presenter.map.getView().setZoom(zoom);
        }
        if (presetState.base_map) {
            const baseMapId = parseInt(presetState.base_map, 10);
            if (!Number.isNaN(baseMapId)) {
                presenter.setBaseMap(baseMapId);
            }
        }
        if (presetState.forecast) {
            const fd = moment.utc(presetState.forecast).format("YYYY-MM-DDTHH:mm") + "Z";
            onSetForecastDate(fd);
        }
        if (presetState.layers) {
            presenter.removeAllLayers();
            reloadAllLayers(presetState.layers);
        }
        if (presetState.lview) {
            const view = LayerSelectorViews[presetState.lview as keyof typeof LayerSelectorViews];
            onSetLayerSelectorView(view);
        }
    }


    return {
        data: presets,
        onLoadPreset,
        onSavePreset,
        onDeletePreset,
        stateToString,
    };
}
